/**
 * Sync Service for handling cloud synchronization
 *
 * Manages the sync queue, processes pending items, and handles
 * conflict resolution using Last Write Wins strategy.
 */

import { getSyncQueue, clearSyncQueue, getDB, getLastSyncTime, setLastSyncTime } from './db.js'
import {
  saveWorkoutToServer,
  deleteWorkoutFromServer,
  syncWorkouts as apiSyncWorkouts,
  batchSyncWorkouts,
  fetchWorkouts,
  saveExerciseToServer,
  deleteExerciseFromServer,
  batchSaveExercisesToServer,
  fetchExerciseLibrary,
  saveWeightRecordToServer,
  deleteWeightRecordFromServer,
  batchSyncWeightRecords,
  fetchWeightRecords,
  fetchDeltaWorkouts,
  fetchDeltaExercises,
  fetchDeltaWeightRecords,
  fetchAllData,
  fetchAllDeltaData
} from './api.js'
import {
  syncTemplateToSupabase,
  deleteTemplateFromSupabase,
  updateTemplateUsageInSupabase
} from './templates.js'

// Sync state
let isSyncing = false
let syncListeners = []

/**
 * Register a listener for sync status changes
 */
export function onSyncStatusChange(callback) {
  syncListeners.push(callback)
  return () => {
    syncListeners = syncListeners.filter(cb => cb !== callback)
  }
}

/**
 * Notify all listeners of sync status change
 */
function notifySyncStatus(status) {
  syncListeners.forEach(callback => callback(status))
}

/**
 * Get current sync status
 */
export function getSyncStatus() {
  return {
    isSyncing,
    isOnline: navigator.onLine
  }
}

/**
 * Get pending sync count
 */
export async function getPendingSyncCount() {
  const queue = await getSyncQueue()
  return queue.length
}

/**
 * Process the sync queue
 * Returns { success: boolean, processed: number, failed: number, errors: array }
 */
export async function processSyncQueue() {
  if (isSyncing) {
    console.log('Sync already in progress')
    return { success: false, processed: 0, failed: 0, errors: ['Sync already in progress'] }
  }

  if (!navigator.onLine) {
    console.log('Offline - cannot sync')
    return { success: false, processed: 0, failed: 0, errors: ['No internet connection'] }
  }

  isSyncing = true
  notifySyncStatus({ isSyncing: true, message: '同步中...' })

  const queue = await getSyncQueue()

  if (queue.length === 0) {
    isSyncing = false
    notifySyncStatus({ isSyncing: false, message: '已同步', success: true })
    return { success: true, processed: 0, failed: 0, errors: [] }
  }

  console.log(`Processing ${queue.length} sync items`)

  let processed = 0
  let failed = 0
  const errors = []
  const processedIds = []

  // Group by action type for batch processing
  const saveActions = queue.filter(item => item.action === 'saveWorkout')
  const deleteActions = queue.filter(item => item.action === 'deleteWorkout')
  const saveExerciseActions = queue.filter(item => item.action === 'saveExerciseLibrary')
  const deleteExerciseActions = queue.filter(item => item.action === 'deleteExerciseLibrary')
  const saveWeightActions = queue.filter(item => item.action === 'saveWeightRecord')
  const deleteWeightActions = queue.filter(item => item.action === 'deleteWeightRecord')

  // Process delete actions first (to avoid conflicts)
  for (const item of deleteActions) {
    try {
      await deleteWorkoutFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete workout:', error)
      errors.push({ id: item.data.id, action: 'delete', error: error.message })
      failed++
    }
  }

  // Process save actions
  // Sort by timestamp to ensure correct order (oldest first)
  saveActions.sort((a, b) => a.timestamp - b.timestamp)

  for (const item of saveActions) {
    try {
      const workoutData = item.data.workout || item.data
      const exercises = item.data.exercises || workoutData.exercises || []

      await saveWorkoutToServer({
        ...workoutData,
        exercises
      })

      // Mark as synced in local DB
      await markWorkoutAsSynced(workoutData.id)

      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to save workout:', error)
      errors.push({ id: item.data.id || item.data.workout?.id, action: 'save', error: error.message })
      failed++
    }
  }

  // Process exercise library delete actions
  for (const item of deleteExerciseActions) {
    try {
      await deleteExerciseFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete exercise:', error)
      errors.push({ id: item.data.id, action: 'deleteExercise', error: error.message })
      failed++
    }
  }

  // Process exercise library save actions
  saveExerciseActions.sort((a, b) => a.timestamp - b.timestamp)

  for (const item of saveExerciseActions) {
    try {
      await saveExerciseToServer(item.data)

      // Mark as synced in local DB
      await markExerciseAsSynced(item.data.id)

      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to save exercise:', error)
      errors.push({ id: item.data.id, action: 'saveExercise', error: error.message })
      failed++
    }
  }

  // Process weight record delete actions
  for (const item of deleteWeightActions) {
    try {
      await deleteWeightRecordFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete weight record:', error)
      errors.push({ id: item.data.id, action: 'deleteWeightRecord', error: error.message })
      failed++
    }
  }

  // Process weight record save actions
  saveWeightActions.sort((a, b) => a.timestamp - b.timestamp)

  for (const item of saveWeightActions) {
    try {
      await saveWeightRecordToServer(item.data)
      await markWeightRecordAsSynced(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to save weight record:', error)
      errors.push({ id: item.data.id, action: 'saveWeightRecord', error: error.message })
      failed++
    }
  }

  // Clear processed items from queue
  if (processedIds.length > 0) {
    await removeFromSyncQueue(processedIds)
  }

  isSyncing = false

  const success = failed === 0
  const message = success
    ? `已同步 ${processed} 筆資料`
    : `同步完成，${failed} 筆失敗`

  notifySyncStatus({
    isSyncing: false,
    message,
    success,
    processed,
    failed
  })

  return { success, processed, failed, errors }
}

/**
 * Mark a workout as synced in local DB
 */
async function markWorkoutAsSynced(workoutId) {
  const db = getDB()
  const workout = await db.get('workouts', workoutId)

  if (workout) {
    workout.synced = true
    await db.put('workouts', workout)
  }
}

/**
 * Mark an exercise as synced in local DB
 */
async function markExerciseAsSynced(exerciseId) {
  const db = getDB()
  const exercise = await db.get('exerciseLibrary', exerciseId)

  if (exercise) {
    exercise.synced = true
    await db.put('exerciseLibrary', exercise)
  }
}

/**
 * Mark a weight record as synced in local DB
 */
async function markWeightRecordAsSynced(recordId) {
  const db = getDB()
  const record = await db.get('weightRecords', recordId)

  if (record) {
    record.synced = true
    await db.put('weightRecords', record)
  }
}

/**
 * Mark a template as synced in local DB
 */
async function markTemplateAsSynced(templateId) {
  const db = getDB()
  const template = await db.get('workoutTemplates', templateId)

  if (template) {
    template.synced = true
    await db.put('workoutTemplates', template)
  }
}

/**
 * Remove specific items from sync queue by their IDs
 */
async function removeFromSyncQueue(ids) {
  const db = getDB()
  const tx = db.transaction('syncQueue', 'readwrite')
  const store = tx.objectStore('syncQueue')

  for (const id of ids) {
    await store.delete(id)
  }

  await tx.done
}

/**
 * Perform a full sync - fetch from server and merge with local
 */
export async function performFullSync() {
  if (!navigator.onLine) {
    return { success: false, error: 'No internet connection' }
  }

  if (isSyncing) {
    return { success: false, error: 'Sync already in progress' }
  }

  isSyncing = true

  try {
    notifySyncStatus({ isSyncing: true, message: '正在同步...' })

    // First, process any pending local changes (upload)
    const queueResult = await processLocalChanges()

    if (!queueResult.success && queueResult.failed > 0) {
      console.warn('Some local changes failed to sync')
    }

    // Then, fetch ALL data from server in a single API call
    notifySyncStatus({ isSyncing: true, message: '下載雲端資料...' })
    const allData = await fetchAllData()

    // Merge all data with local
    notifySyncStatus({ isSyncing: true, message: '合併資料...' })
    const totalDownloaded = await mergeAllDataWithLocal(allData)

    isSyncing = false

    const message = totalDownloaded > 0
      ? `已同步，下載 ${totalDownloaded} 筆資料`
      : '已同步'

    notifySyncStatus({
      isSyncing: false,
      message,
      success: true
    })

    return {
      success: true,
      uploaded: queueResult.processed,
      downloaded: totalDownloaded
    }
  } catch (error) {
    console.error('Full sync failed:', error)
    isSyncing = false
    notifySyncStatus({
      isSyncing: false,
      message: '同步失敗',
      success: false
    })
    return { success: false, error: error.message }
  }
}

// Batch size constants to avoid GAS timeout (30 seconds limit)
// Increased batch sizes for better performance with optimized soft delete
const BATCH_SIZE_WORKOUTS = 20 // Process 20 workouts per batch (increased from 10)
const BATCH_SIZE_EXERCISES = 50 // Process 50 exercises per batch
const BATCH_SIZE_WEIGHT_RECORDS = 40 // Process 40 weight records per batch (increased from 20)

/**
 * Process local changes (upload to server) - OPTIMIZED with chunked batch processing
 * Similar to processSyncQueue but doesn't set isSyncing flag
 */
async function processLocalChanges() {
  const queue = await getSyncQueue()

  if (queue.length === 0) {
    return { success: true, processed: 0, failed: 0, errors: [] }
  }

  console.log(`Uploading ${queue.length} local changes`)

  let processed = 0
  let failed = 0
  const errors = []
  const processedIds = []

  // Group by action type
  const saveActions = queue.filter(item => item.action === 'saveWorkout')
  const deleteActions = queue.filter(item => item.action === 'deleteWorkout')
  const saveExerciseActions = queue.filter(item => item.action === 'saveExerciseLibrary')
  const deleteExerciseActions = queue.filter(item => item.action === 'deleteExerciseLibrary')
  const saveWeightActions = queue.filter(item => item.action === 'saveWeightRecord')
  const deleteWeightActions = queue.filter(item => item.action === 'deleteWeightRecord')
  const saveTemplateActions = queue.filter(item => item.action === 'saveTemplate')
  const deleteTemplateActions = queue.filter(item => item.action === 'deleteTemplate')
  const updateTemplateUsageActions = queue.filter(item => item.action === 'updateTemplateUsage')

  // Process delete actions first (keep individual for now - usually low volume)
  for (const item of deleteActions) {
    try {
      await deleteWorkoutFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete workout:', error)
      errors.push({ id: item.data.id, action: 'delete', error: error.message })
      failed++
    }
  }

  // BATCH PROCESS: Save workouts (with chunking to avoid timeout)
  if (saveActions.length > 0) {
    saveActions.sort((a, b) => a.timestamp - b.timestamp)

    // Deduplicate workouts by ID, keeping the latest (most recent timestamp)
    const workoutMap = new Map()
    for (const item of saveActions) {
      const workoutData = item.data.workout || item.data
      const workoutId = workoutData.id

      if (!workoutMap.has(workoutId) || item.timestamp > workoutMap.get(workoutId).timestamp) {
        workoutMap.set(workoutId, item)
      }
    }

    const deduplicatedActions = Array.from(workoutMap.values())
    console.log(`Deduplicated ${saveActions.length} save actions to ${deduplicatedActions.length} unique workouts`)

    // Split into chunks to avoid GAS timeout
    const chunks = []
    for (let i = 0; i < deduplicatedActions.length; i += BATCH_SIZE_WORKOUTS) {
      chunks.push(deduplicatedActions.slice(i, i + BATCH_SIZE_WORKOUTS))
    }

    console.log(`Processing ${deduplicatedActions.length} workouts in ${chunks.length} chunks`)

    for (const chunk of chunks) {
      const workoutsToSync = chunk.map(item => {
        const workoutData = item.data.workout || item.data
        const exercises = item.data.exercises || workoutData.exercises || []
        return {
          ...workoutData,
          exercises
        }
      })

      try {
        console.log(`Batch syncing ${workoutsToSync.length} workouts`)
        const result = await batchSyncWorkouts(workoutsToSync)

        // Mark all in this chunk as synced
        for (const item of chunk) {
          const workoutData = item.data.workout || item.data
          await markWorkoutAsSynced(workoutData.id)
          processedIds.push(item.id)
          processed++
        }
      } catch (error) {
        console.error('Batch sync workouts failed, falling back to individual sync:', error)

        // Fallback: try individual sync for this chunk
        for (const item of chunk) {
          try {
            const workoutData = item.data.workout || item.data
            const exercises = item.data.exercises || workoutData.exercises || []

            await saveWorkoutToServer({
              ...workoutData,
              exercises
            })

            await markWorkoutAsSynced(workoutData.id)
            processedIds.push(item.id)
            processed++
          } catch (individualError) {
            console.error('Failed to save workout individually:', individualError)
            errors.push({
              id: item.data.id || item.data.workout?.id,
              action: 'save',
              error: individualError.message
            })
            failed++
          }
        }
      }
    }
  }

  // Process exercise library delete actions (individual - low volume)
  for (const item of deleteExerciseActions) {
    try {
      await deleteExerciseFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete exercise:', error)
      errors.push({ id: item.data.id, action: 'deleteExercise', error: error.message })
      failed++
    }
  }

  // BATCH PROCESS: Save exercises (with chunking to avoid timeout)
  if (saveExerciseActions.length > 0) {
    saveExerciseActions.sort((a, b) => a.timestamp - b.timestamp)

    // Deduplicate exercises by ID, keeping the latest (most recent timestamp)
    const exerciseMap = new Map()
    for (const item of saveExerciseActions) {
      const exerciseId = item.data.id

      if (!exerciseMap.has(exerciseId) || item.timestamp > exerciseMap.get(exerciseId).timestamp) {
        exerciseMap.set(exerciseId, item)
      }
    }

    const deduplicatedActions = Array.from(exerciseMap.values())
    console.log(`Deduplicated ${saveExerciseActions.length} save exercise actions to ${deduplicatedActions.length} unique exercises`)

    // Split into chunks to avoid timeout
    const chunks = []
    for (let i = 0; i < deduplicatedActions.length; i += BATCH_SIZE_EXERCISES) {
      chunks.push(deduplicatedActions.slice(i, i + BATCH_SIZE_EXERCISES))
    }

    console.log(`Processing ${deduplicatedActions.length} exercises in ${chunks.length} chunks`)

    for (const chunk of chunks) {
      const exercisesToSync = chunk.map(item => item.data)

      try {
        console.log(`Batch syncing ${exercisesToSync.length} exercises`)
        await batchSaveExercisesToServer(exercisesToSync)

        // Mark all in this chunk as synced
        for (const item of chunk) {
          await markExerciseAsSynced(item.data.id)
          processedIds.push(item.id)
          processed++
        }
      } catch (error) {
        console.error('Batch sync exercises failed, falling back to individual sync:', error)

        // Fallback: try individual sync for this chunk
        for (const item of chunk) {
          try {
            await saveExerciseToServer(item.data)
            await markExerciseAsSynced(item.data.id)
            processedIds.push(item.id)
            processed++
          } catch (individualError) {
            console.error('Failed to save exercise individually:', individualError)
            errors.push({
              id: item.data.id,
              action: 'saveExercise',
              error: individualError.message
            })
            failed++
          }
        }
      }
    }
  }

  // Process weight record delete actions (individual - low volume)
  for (const item of deleteWeightActions) {
    try {
      await deleteWeightRecordFromServer(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete weight record:', error)
      errors.push({ id: item.data.id, action: 'deleteWeightRecord', error: error.message })
      failed++
    }
  }

  // BATCH PROCESS: Save weight records (with chunking to avoid timeout)
  if (saveWeightActions.length > 0) {
    saveWeightActions.sort((a, b) => a.timestamp - b.timestamp)

    // Split into chunks to avoid GAS timeout
    const chunks = []
    for (let i = 0; i < saveWeightActions.length; i += BATCH_SIZE_WEIGHT_RECORDS) {
      chunks.push(saveWeightActions.slice(i, i + BATCH_SIZE_WEIGHT_RECORDS))
    }

    console.log(`Processing ${saveWeightActions.length} weight records in ${chunks.length} chunks`)

    for (const chunk of chunks) {
      const recordsToSync = chunk.map(item => item.data)

      try {
        console.log(`Batch syncing ${recordsToSync.length} weight records`)
        const result = await batchSyncWeightRecords(recordsToSync)

        // Mark all in this chunk as synced
        for (const item of chunk) {
          await markWeightRecordAsSynced(item.data.id)
          processedIds.push(item.id)
          processed++
        }
      } catch (error) {
        console.error('Batch sync weight records failed, falling back to individual sync:', error)

        // Fallback: try individual sync for this chunk
        for (const item of chunk) {
          try {
            await saveWeightRecordToServer(item.data)
            await markWeightRecordAsSynced(item.data.id)
            processedIds.push(item.id)
            processed++
          } catch (individualError) {
            console.error('Failed to save weight record individually:', individualError)
            errors.push({
              id: item.data.id,
              action: 'saveWeightRecord',
              error: individualError.message
            })
            failed++
          }
        }
      }
    }
  }

  // Process template delete actions (individual - low volume)
  for (const item of deleteTemplateActions) {
    try {
      await deleteTemplateFromSupabase(item.data.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to delete template:', error)
      errors.push({ id: item.data.id, action: 'deleteTemplate', error: error.message })
      failed++
    }
  }

  // Process template save actions (individual for now - usually low volume)
  saveTemplateActions.sort((a, b) => a.timestamp - b.timestamp)

  for (const item of saveTemplateActions) {
    try {
      const templateData = item.data.template || item.data
      const exercises = item.data.exercises || []

      await syncTemplateToSupabase(templateData, exercises)
      await markTemplateAsSynced(templateData.id)
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to save template:', error)
      errors.push({ id: item.data.id || item.data.template?.id, action: 'saveTemplate', error: error.message })
      failed++
    }
  }

  // Process template usage update actions
  for (const item of updateTemplateUsageActions) {
    try {
      await updateTemplateUsageInSupabase(
        item.data.id,
        item.data.use_count,
        item.data.last_used_at
      )
      processedIds.push(item.id)
      processed++
    } catch (error) {
      console.error('Failed to update template usage:', error)
      errors.push({ id: item.data.id, action: 'updateTemplateUsage', error: error.message })
      failed++
    }
  }

  // Clear processed items
  if (processedIds.length > 0) {
    await removeFromSyncQueue(processedIds)
  }

  return { success: failed === 0, processed, failed, errors }
}

/**
 * Merge all data from server with local using Last Write Wins strategy
 * Handles workouts, exercises, weight records, and templates
 */
async function mergeAllDataWithLocal(allData) {
  const db = getDB()
  let totalDownloaded = 0

  // Process workouts
  for (const serverWorkout of allData.workouts) {
    const localWorkout = await db.get('workouts', serverWorkout.id)

    let shouldUpdate = false

    if (!localWorkout) {
      shouldUpdate = true
    } else if (localWorkout.synced === false) {
      shouldUpdate = false
    } else {
      const serverTime = new Date(serverWorkout.updated_at || serverWorkout.date).getTime()
      const localTime = new Date(localWorkout.updatedAt || localWorkout.date).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      const workoutToSave = {
        id: serverWorkout.id,
        date: serverWorkout.date,
        type: serverWorkout.type,
        duration: serverWorkout.duration,
        notes: serverWorkout.notes,
        template_id: serverWorkout.template_id,
        synced: true,
        updatedAt: serverWorkout.updated_at || new Date().toISOString(),
        createdAt: serverWorkout.created_at || localWorkout?.createdAt || serverWorkout.date
      }

      await db.put('workouts', workoutToSave)

      // Save exercises
      if (serverWorkout.exercises && serverWorkout.exercises.length > 0) {
        const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', serverWorkout.id)
        for (const ex of existingExercises) {
          await db.delete('exercises', ex.id)
        }

        for (const exercise of serverWorkout.exercises) {
          let sets = exercise.sets
          if (typeof sets === 'string' && sets.startsWith('[')) {
            try {
              sets = JSON.parse(sets)
            } catch (e) {
              // Keep as is
            }
          }

          await db.put('exercises', {
            id: exercise.id,
            workout_id: serverWorkout.id,
            exercise_name: exercise.exercise_name,
            sets: sets,
            rest_time: exercise.rest_time,
            notes: exercise.notes
          })
        }
      }

      totalDownloaded++
    }
  }

  // Process exercise library
  for (const serverExercise of allData.exerciseLibrary) {
    const localExercise = await db.get('exerciseLibrary', serverExercise.id)

    let shouldUpdate = false

    if (!localExercise) {
      shouldUpdate = true
    } else if (localExercise.synced === false) {
      shouldUpdate = false
    } else {
      const serverTime = new Date(serverExercise.updated_at || serverExercise.created_at).getTime()
      const localTime = new Date(localExercise.updatedAt || localExercise.createdAt).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      await db.put('exerciseLibrary', {
        id: serverExercise.id,
        name: serverExercise.name,
        muscleGroup: serverExercise.muscle_group,
        description: serverExercise.description || '',
        isCustom: serverExercise.is_custom !== undefined ? serverExercise.is_custom : true,
        synced: true,
        updatedAt: serverExercise.updated_at || new Date().toISOString(),
        createdAt: serverExercise.created_at || localExercise?.createdAt || new Date().toISOString()
      })
      totalDownloaded++
    }
  }

  // Process weight records
  for (const serverRecord of allData.weightRecords) {
    const localRecord = await db.get('weightRecords', serverRecord.id)

    let shouldUpdate = false

    if (!localRecord) {
      shouldUpdate = true
    } else if (localRecord.synced === false) {
      shouldUpdate = false
    } else {
      const serverTime = new Date(serverRecord.updated_at || serverRecord.created_at).getTime()
      const localTime = new Date(localRecord.updatedAt || localRecord.createdAt).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      await db.put('weightRecords', {
        id: serverRecord.id,
        weight: serverRecord.weight,
        bodyFat: serverRecord.bodyFat || null,
        date: serverRecord.date,
        synced: true,
        updatedAt: serverRecord.updated_at || new Date().toISOString(),
        createdAt: serverRecord.created_at || localRecord?.createdAt || new Date().toISOString()
      })
      totalDownloaded++
    }
  }

  // Process templates
  for (const serverTemplate of allData.templates) {
    const localTemplate = await db.get('workoutTemplates', serverTemplate.id)

    let shouldUpdate = false

    if (!localTemplate) {
      shouldUpdate = true
    } else if (localTemplate.synced === false) {
      shouldUpdate = false
    } else {
      const serverTime = new Date(serverTemplate.updated_at || serverTemplate.created_at).getTime()
      const localTime = new Date(localTemplate.updated_at || localTemplate.created_at).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      const templateToSave = {
        id: serverTemplate.id,
        name: serverTemplate.name,
        description: serverTemplate.description || '',
        use_count: serverTemplate.use_count || 0,
        last_used_at: serverTemplate.last_used_at,
        synced: true,
        updated_at: serverTemplate.updated_at || new Date().toISOString(),
        created_at: serverTemplate.created_at || localTemplate?.created_at || new Date().toISOString()
      }

      await db.put('workoutTemplates', templateToSave)

      // Save template exercises
      if (serverTemplate.template_exercises && serverTemplate.template_exercises.length > 0) {
        const existingExercises = await db.getAllFromIndex('templateExercises', 'template_id', serverTemplate.id)
        for (const ex of existingExercises) {
          await db.delete('templateExercises', ex.id)
        }

        for (const exercise of serverTemplate.template_exercises) {
          await db.put('templateExercises', {
            id: exercise.id,
            template_id: serverTemplate.id,
            exercise_name: exercise.exercise_name,
            order_index: exercise.order_index,
            sets: exercise.sets,
            rest_time: exercise.rest_time,
            notes: exercise.notes || ''
          })
        }
      }

      totalDownloaded++
    }
  }

  console.log(`Downloaded ${totalDownloaded} items from server`)
  return totalDownloaded
}

/**
 * Download workouts from server and merge with local
 * Uses Last Write Wins strategy based on updatedAt timestamp
 */
async function downloadFromServer() {
  const db = getDB()

  // Fetch all workouts from server
  const serverWorkouts = await fetchWorkouts()

  if (!serverWorkouts || serverWorkouts.length === 0) {
    return { downloaded: 0 }
  }

  console.log(`Received ${serverWorkouts.length} workouts from server`)

  let downloaded = 0

  for (const serverWorkout of serverWorkouts) {
    const localWorkout = await db.get('workouts', serverWorkout.id)

    // Determine if we should update local
    let shouldUpdate = false

    if (!localWorkout) {
      // New workout from server
      shouldUpdate = true
    } else if (localWorkout.synced === false) {
      // Local has unsynced changes, skip (will be uploaded next time)
      shouldUpdate = false
    } else {
      // Compare timestamps - Last Write Wins
      const serverTime = new Date(serverWorkout.updatedAt || serverWorkout.date).getTime()
      const localTime = new Date(localWorkout.updatedAt || localWorkout.date).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      // Save workout to local DB
      const workoutToSave = {
        id: serverWorkout.id,
        date: serverWorkout.date,
        type: serverWorkout.type,
        duration: serverWorkout.duration,
        notes: serverWorkout.notes,
        synced: true,
        updatedAt: serverWorkout.updatedAt || new Date().toISOString(),
        createdAt: serverWorkout.createdAt || localWorkout?.createdAt || serverWorkout.date
      }

      await db.put('workouts', workoutToSave)

      // Save exercises if provided
      if (serverWorkout.exercises && serverWorkout.exercises.length > 0) {
        // Delete existing exercises for this workout
        const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', serverWorkout.id)
        for (const ex of existingExercises) {
          await db.delete('exercises', ex.id)
        }

        // Add new exercises
        for (const exercise of serverWorkout.exercises) {
          // Handle both old and new format for sets
          let sets = exercise.sets
          // If sets is a string (JSON from server), parse it
          if (typeof sets === 'string' && sets.startsWith('[')) {
            try {
              sets = JSON.parse(sets)
            } catch (e) {
              // Keep as is if parsing fails
            }
          }

          await db.put('exercises', {
            id: exercise.id,
            workout_id: serverWorkout.id,
            exercise_name: exercise.exercise_name,
            sets: sets,
            reps: exercise.reps,
            weight: exercise.weight,
            rest_time: exercise.rest_time
          })
        }
      }

      downloaded++
    }
  }

  console.log(`Downloaded ${downloaded} workouts from server`)
  return { downloaded }
}

/**
 * Download exercise library from server and merge with local
 * Uses Last Write Wins strategy based on updatedAt timestamp
 * Only syncs custom exercises (isCustom = true)
 */
async function downloadExerciseLibrary() {
  const db = getDB()

  // Fetch all custom exercises from server
  const serverExercises = await fetchExerciseLibrary()

  if (!serverExercises || serverExercises.length === 0) {
    return { downloaded: 0 }
  }

  console.log(`Received ${serverExercises.length} exercises from server`)

  let downloaded = 0

  for (const serverExercise of serverExercises) {
    const localExercise = await db.get('exerciseLibrary', serverExercise.id)

    // Determine if we should update local
    let shouldUpdate = false

    if (!localExercise) {
      // New exercise from server
      shouldUpdate = true
    } else if (localExercise.synced === false) {
      // Local has unsynced changes, skip (will be uploaded next time)
      shouldUpdate = false
    } else {
      // Compare timestamps - Last Write Wins
      const serverTime = new Date(serverExercise.updatedAt || serverExercise.createdAt).getTime()
      const localTime = new Date(localExercise.updatedAt || localExercise.createdAt).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      // Save exercise to local DB
      const exerciseToSave = {
        id: serverExercise.id,
        name: serverExercise.name,
        muscleGroup: serverExercise.muscleGroup,
        description: serverExercise.description || '',
        isCustom: true, // Only custom exercises are synced
        synced: true,
        updatedAt: serverExercise.updatedAt || new Date().toISOString(),
        createdAt: serverExercise.createdAt || localExercise?.createdAt || new Date().toISOString()
      }

      await db.put('exerciseLibrary', exerciseToSave)
      downloaded++
    }
  }

  console.log(`Downloaded ${downloaded} exercises from server`)
  return { downloaded }
}

/**
 * Perform delta sync - only fetch changed data since last sync
 * This is much faster than full sync for incremental updates
 */
export async function performDeltaSync() {
  if (!navigator.onLine) {
    return { success: false, error: 'No internet connection' }
  }

  if (isSyncing) {
    return { success: false, error: 'Sync already in progress' }
  }

  isSyncing = true

  try {
    notifySyncStatus({ isSyncing: true, message: '正在同步...' })

    // First, process any pending local changes (upload)
    const queueResult = await processLocalChanges()

    if (!queueResult.success && queueResult.failed > 0) {
      console.warn('Some local changes failed to sync')
    }

    // Get the last sync time (use the oldest one to be safe)
    const workoutsLastSync = await getLastSyncTime('workouts')
    const exercisesLastSync = await getLastSyncTime('exercises')
    const weightLastSync = await getLastSyncTime('weightRecords')
    const templatesLastSync = await getLastSyncTime('templates')

    const lastSyncTime = [workoutsLastSync, exercisesLastSync, weightLastSync, templatesLastSync]
      .filter(Boolean)
      .sort()[0] || null

    // Fetch ALL delta data in a single API call
    notifySyncStatus({ isSyncing: true, message: '下載雲端資料...' })
    const deltaData = await fetchAllDeltaData(lastSyncTime)

    // Merge delta data with local
    notifySyncStatus({ isSyncing: true, message: '合併資料...' })
    const totalDownloaded = await mergeDeltaDataWithLocal(deltaData)

    // Update last sync time for all entity types
    await setLastSyncTime('workouts', deltaData.syncTime)
    await setLastSyncTime('exercises', deltaData.syncTime)
    await setLastSyncTime('weightRecords', deltaData.syncTime)
    await setLastSyncTime('templates', deltaData.syncTime)

    isSyncing = false

    const message = totalDownloaded > 0
      ? `已同步，下載 ${totalDownloaded} 筆資料`
      : '已同步'

    notifySyncStatus({
      isSyncing: false,
      message,
      success: true
    })

    return {
      success: true,
      uploaded: queueResult.processed,
      downloaded: totalDownloaded
    }
  } catch (error) {
    console.error('Delta sync failed:', error)
    isSyncing = false
    notifySyncStatus({
      isSyncing: false,
      message: '同步失敗',
      success: false
    })
    return { success: false, error: error.message }
  }
}

/**
 * Merge delta data from server with local using Last Write Wins strategy
 * Handles workouts, exercises, weight records, and templates
 */
async function mergeDeltaDataWithLocal(deltaData) {
  const db = getDB()
  let totalDownloaded = 0

  // Process workouts (added and modified)
  const changedWorkouts = [...deltaData.workouts.added, ...deltaData.workouts.modified]
  for (const serverWorkout of changedWorkouts) {
    const localWorkout = await db.get('workouts', serverWorkout.id)

    if (localWorkout && localWorkout.synced === false) {
      continue // Skip - local changes take precedence
    }

    const workoutToSave = {
      id: serverWorkout.id,
      date: serverWorkout.date,
      type: serverWorkout.type,
      duration: serverWorkout.duration,
      notes: serverWorkout.notes,
      template_id: serverWorkout.template_id,
      synced: true,
      updatedAt: serverWorkout.updated_at || new Date().toISOString(),
      createdAt: serverWorkout.created_at || localWorkout?.createdAt || serverWorkout.date
    }

    await db.put('workouts', workoutToSave)

    // Save exercises
    if (serverWorkout.exercises && serverWorkout.exercises.length > 0) {
      const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', serverWorkout.id)
      for (const ex of existingExercises) {
        await db.delete('exercises', ex.id)
      }

      for (const exercise of serverWorkout.exercises) {
        let sets = exercise.sets
        if (typeof sets === 'string' && sets.startsWith('[')) {
          try {
            sets = JSON.parse(sets)
          } catch (e) {
            // Keep as is
          }
        }

        await db.put('exercises', {
          id: exercise.id,
          workout_id: serverWorkout.id,
          exercise_name: exercise.exercise_name,
          sets: sets,
          rest_time: exercise.rest_time,
          notes: exercise.notes
        })
      }
    }

    totalDownloaded++
  }

  // Process deleted workouts
  for (const workoutId of deltaData.workouts.deleted) {
    const localWorkout = await db.get('workouts', workoutId)
    if (localWorkout) {
      await db.delete('workouts', workoutId)
      const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', workoutId)
      for (const ex of existingExercises) {
        await db.delete('exercises', ex.id)
      }
      totalDownloaded++
    }
  }

  // Process exercises (added and modified)
  const changedExercises = [...deltaData.exerciseLibrary.added, ...deltaData.exerciseLibrary.modified]
  for (const serverExercise of changedExercises) {
    const localExercise = await db.get('exerciseLibrary', serverExercise.id)

    if (localExercise && localExercise.synced === false) {
      continue // Skip - local changes take precedence
    }

    await db.put('exerciseLibrary', {
      id: serverExercise.id,
      name: serverExercise.name,
      muscleGroup: serverExercise.muscle_group,
      description: serverExercise.description || '',
      isCustom: serverExercise.is_custom !== undefined ? serverExercise.is_custom : true,
      synced: true,
      updatedAt: serverExercise.updated_at || new Date().toISOString(),
      createdAt: serverExercise.created_at || localExercise?.createdAt || new Date().toISOString()
    })
    totalDownloaded++
  }

  // Process deleted exercises
  for (const exerciseId of deltaData.exerciseLibrary.deleted) {
    const localExercise = await db.get('exerciseLibrary', exerciseId)
    if (localExercise) {
      await db.delete('exerciseLibrary', exerciseId)
      totalDownloaded++
    }
  }

  // Process weight records (added and modified)
  const changedRecords = [...deltaData.weightRecords.added, ...deltaData.weightRecords.modified]
  for (const serverRecord of changedRecords) {
    const localRecord = await db.get('weightRecords', serverRecord.id)

    if (localRecord && localRecord.synced === false) {
      continue // Skip - local changes take precedence
    }

    await db.put('weightRecords', {
      id: serverRecord.id,
      weight: serverRecord.weight,
      bodyFat: serverRecord.bodyFat || null,
      date: serverRecord.date,
      synced: true,
      updatedAt: serverRecord.updated_at || new Date().toISOString(),
      createdAt: serverRecord.created_at || localRecord?.createdAt || new Date().toISOString()
    })
    totalDownloaded++
  }

  // Process deleted weight records
  for (const recordId of deltaData.weightRecords.deleted) {
    const localRecord = await db.get('weightRecords', recordId)
    if (localRecord) {
      await db.delete('weightRecords', recordId)
      totalDownloaded++
    }
  }

  // Process templates (added and modified)
  const changedTemplates = [...deltaData.templates.added, ...deltaData.templates.modified]
  for (const serverTemplate of changedTemplates) {
    const localTemplate = await db.get('workoutTemplates', serverTemplate.id)

    if (localTemplate && localTemplate.synced === false) {
      continue // Skip - local changes take precedence
    }

    const templateToSave = {
      id: serverTemplate.id,
      name: serverTemplate.name,
      description: serverTemplate.description || '',
      use_count: serverTemplate.use_count || 0,
      last_used_at: serverTemplate.last_used_at,
      synced: true,
      updated_at: serverTemplate.updated_at || new Date().toISOString(),
      created_at: serverTemplate.created_at || localTemplate?.created_at || new Date().toISOString()
    }

    await db.put('workoutTemplates', templateToSave)

    // Save template exercises
    if (serverTemplate.template_exercises && serverTemplate.template_exercises.length > 0) {
      const existingExercises = await db.getAllFromIndex('templateExercises', 'template_id', serverTemplate.id)
      for (const ex of existingExercises) {
        await db.delete('templateExercises', ex.id)
      }

      for (const exercise of serverTemplate.template_exercises) {
        await db.put('templateExercises', {
          id: exercise.id,
          template_id: serverTemplate.id,
          exercise_name: exercise.exercise_name,
          order_index: exercise.order_index,
          sets: exercise.sets,
          rest_time: exercise.rest_time,
          notes: exercise.notes || ''
        })
      }
    }

    totalDownloaded++
  }

  // Process deleted templates
  for (const templateId of deltaData.templates.deleted) {
    const localTemplate = await db.get('workoutTemplates', templateId)
    if (localTemplate) {
      await db.delete('workoutTemplates', templateId)
      const existingExercises = await db.getAllFromIndex('templateExercises', 'template_id', templateId)
      for (const ex of existingExercises) {
        await db.delete('templateExercises', ex.id)
      }
      totalDownloaded++
    }
  }

  console.log(`Downloaded ${totalDownloaded} items (delta sync)`)
  return totalDownloaded
}

/**
 * Download delta workouts from server
 */
async function downloadDeltaWorkouts() {
  const db = getDB()
  const lastSyncTime = await getLastSyncTime('workouts')

  console.log(`Fetching delta workouts since ${lastSyncTime || 'beginning'}`)

  const delta = await fetchDeltaWorkouts(lastSyncTime)

  let downloaded = 0

  // Process deleted workouts
  for (const workoutId of delta.deleted) {
    const localWorkout = await db.get('workouts', workoutId)
    if (localWorkout) {
      // Delete workout and its exercises
      await db.delete('workouts', workoutId)

      const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', workoutId)
      for (const ex of existingExercises) {
        await db.delete('exercises', ex.id)
      }

      downloaded++
    }
  }

  // Process added and modified workouts
  const changedWorkouts = [...delta.added, ...delta.modified]

  for (const serverWorkout of changedWorkouts) {
    const localWorkout = await db.get('workouts', serverWorkout.id)

    // Check if local has unsynced changes
    if (localWorkout && localWorkout.synced === false) {
      // Skip - local changes take precedence
      continue
    }

    // Save workout to local DB
    const workoutToSave = {
      id: serverWorkout.id,
      date: serverWorkout.date,
      type: serverWorkout.type,
      duration: serverWorkout.duration,
      notes: serverWorkout.notes,
      synced: true,
      updatedAt: serverWorkout.updatedAt || new Date().toISOString(),
      createdAt: serverWorkout.createdAt || localWorkout?.createdAt || serverWorkout.date
    }

    await db.put('workouts', workoutToSave)

    // Save exercises if provided
    if (serverWorkout.exercises && serverWorkout.exercises.length > 0) {
      // Delete existing exercises for this workout
      const existingExercises = await db.getAllFromIndex('exercises', 'workout_id', serverWorkout.id)
      for (const ex of existingExercises) {
        await db.delete('exercises', ex.id)
      }

      // Add new exercises
      for (const exercise of serverWorkout.exercises) {
        let sets = exercise.sets
        if (typeof sets === 'string' && sets.startsWith('[')) {
          try {
            sets = JSON.parse(sets)
          } catch (e) {
            // Keep as is if parsing fails
          }
        }

        await db.put('exercises', {
          id: exercise.id,
          workout_id: serverWorkout.id,
          exercise_name: exercise.exercise_name,
          sets: sets,
          reps: exercise.reps,
          weight: exercise.weight,
          rest_time: exercise.rest_time
        })
      }
    }

    downloaded++
  }

  // Update last sync time
  await setLastSyncTime('workouts', delta.syncTime)

  console.log(`Downloaded ${downloaded} workouts (delta sync)`)
  return { downloaded }
}

/**
 * Download delta exercises from server
 */
async function downloadDeltaExercises() {
  const db = getDB()
  const lastSyncTime = await getLastSyncTime('exercises')

  console.log(`Fetching delta exercises since ${lastSyncTime || 'beginning'}`)

  const delta = await fetchDeltaExercises(lastSyncTime)

  let downloaded = 0

  // Process deleted exercises
  for (const exerciseId of delta.deleted) {
    const localExercise = await db.get('exerciseLibrary', exerciseId)
    if (localExercise) {
      await db.delete('exerciseLibrary', exerciseId)
      downloaded++
    }
  }

  // Process added and modified exercises
  const changedExercises = [...delta.added, ...delta.modified]

  for (const serverExercise of changedExercises) {
    const localExercise = await db.get('exerciseLibrary', serverExercise.id)

    // Check if local has unsynced changes
    if (localExercise && localExercise.synced === false) {
      // Skip - local changes take precedence
      continue
    }

    // Save exercise to local DB
    const exerciseToSave = {
      id: serverExercise.id,
      name: serverExercise.name,
      muscleGroup: serverExercise.muscleGroup,
      description: serverExercise.description || '',
      isCustom: true,
      synced: true,
      updatedAt: serverExercise.updatedAt || new Date().toISOString(),
      createdAt: serverExercise.createdAt || localExercise?.createdAt || new Date().toISOString()
    }

    await db.put('exerciseLibrary', exerciseToSave)
    downloaded++
  }

  // Update last sync time
  await setLastSyncTime('exercises', delta.syncTime)

  console.log(`Downloaded ${downloaded} exercises (delta sync)`)
  return { downloaded }
}

/**
 * Download delta weight records from server
 */
async function downloadDeltaWeightRecords() {
  const db = getDB()
  const lastSyncTime = await getLastSyncTime('weightRecords')

  console.log(`Fetching delta weight records since ${lastSyncTime || 'beginning'}`)

  const delta = await fetchDeltaWeightRecords(lastSyncTime)

  let downloaded = 0

  // Process deleted records
  for (const recordId of delta.deleted) {
    const localRecord = await db.get('weightRecords', recordId)
    if (localRecord) {
      await db.delete('weightRecords', recordId)
      downloaded++
    }
  }

  // Process added and modified records
  const changedRecords = [...delta.added, ...delta.modified]

  for (const serverRecord of changedRecords) {
    const localRecord = await db.get('weightRecords', serverRecord.id)

    // Check if local has unsynced changes
    if (localRecord && localRecord.synced === false) {
      // Skip - local changes take precedence
      continue
    }

    // Save record to local DB
    const recordToSave = {
      id: serverRecord.id,
      weight: serverRecord.weight,
      bodyFat: serverRecord.bodyFat || null,
      date: serverRecord.date,
      synced: true,
      updatedAt: serverRecord.updatedAt || new Date().toISOString(),
      createdAt: serverRecord.createdAt || localRecord?.createdAt || new Date().toISOString()
    }

    await db.put('weightRecords', recordToSave)
    downloaded++
  }

  // Update last sync time
  await setLastSyncTime('weightRecords', delta.syncTime)

  console.log(`Downloaded ${downloaded} weight records (delta sync)`)
  return { downloaded }
}

/**
 * Auto-sync when coming back online
 * @param {boolean} runInitialSync - Whether to run initial sync (only when logged in)
 */
export function setupAutoSync(runInitialSync = false) {
  // Sync when coming back online
  window.addEventListener('online', async () => {
    console.log('Back online - starting delta sync')
    await performSmartSync()
  })

  // Update status when going offline
  window.addEventListener('offline', () => {
    notifySyncStatus({
      isSyncing: false,
      message: '離線模式',
      success: false
    })
  })

  // Initial sync if online and logged in
  if (runInitialSync && navigator.onLine) {
    // Delay initial sync slightly to let app initialize
    setTimeout(async () => {
      console.log('Starting initial smart sync...')
      await performSmartSync()
    }, 2000)
  }
}

/**
 * Smart sync - automatically chooses between delta sync and full sync
 * Uses delta sync when lastSyncTime exists, falls back to full sync otherwise
 */
export async function performSmartSync() {
  if (!navigator.onLine) {
    return { success: false, error: 'No internet connection' }
  }

  // Check if we have lastSyncTime for all entity types
  const workoutsLastSync = await getLastSyncTime('workouts')
  const exercisesLastSync = await getLastSyncTime('exercises')
  const weightLastSync = await getLastSyncTime('weightRecords')

  // If any entity type has never synced, do full sync
  if (!workoutsLastSync && !exercisesLastSync && !weightLastSync) {
    console.log('No previous sync detected - performing full sync')
    return await performFullSync()
  }

  // Try delta sync first
  console.log('Performing delta sync...')
  const result = await performDeltaSync()

  // If delta sync fails, fallback to full sync
  if (!result.success) {
    console.warn('Delta sync failed, falling back to full sync')
    return await performFullSync()
  }

  return result
}

/**
 * Download weight records from server and merge with local
 * Uses Last Write Wins strategy based on updatedAt timestamp
 */
async function downloadWeightRecords() {
  const db = getDB()

  // Fetch all weight records from server
  const serverRecords = await fetchWeightRecords()

  if (!serverRecords || serverRecords.length === 0) {
    return { downloaded: 0 }
  }

  console.log(`Received ${serverRecords.length} weight records from server`)

  let downloaded = 0

  for (const serverRecord of serverRecords) {
    const localRecord = await db.get('weightRecords', serverRecord.id)

    // Determine if we should update local
    let shouldUpdate = false

    if (!localRecord) {
      // New record from server
      shouldUpdate = true
    } else if (localRecord.synced === false) {
      // Local has unsynced changes, skip (will be uploaded next time)
      shouldUpdate = false
    } else {
      // Compare timestamps - Last Write Wins
      const serverTime = new Date(serverRecord.updatedAt || serverRecord.createdAt).getTime()
      const localTime = new Date(localRecord.updatedAt || localRecord.createdAt).getTime()

      if (serverTime > localTime) {
        shouldUpdate = true
      }
    }

    if (shouldUpdate) {
      // Save weight record to local DB
      const recordToSave = {
        id: serverRecord.id,
        weight: serverRecord.weight,
        bodyFat: serverRecord.bodyFat || null,
        date: serverRecord.date,
        synced: true,
        updatedAt: serverRecord.updatedAt || new Date().toISOString(),
        createdAt: serverRecord.createdAt || localRecord?.createdAt || new Date().toISOString()
      }

      await db.put('weightRecords', recordToSave)
      downloaded++
    }
  }

  console.log(`Downloaded ${downloaded} weight records from server`)
  return { downloaded }
}

/**
 * Request background sync (if supported)
 */
export async function requestBackgroundSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try {
      const registration = await navigator.serviceWorker.ready
      await registration.sync.register('sync-workouts')
      console.log('Background sync registered')
      return true
    } catch (error) {
      console.error('Background sync registration failed:', error)
      return false
    }
  }
  return false
}
